/**
 * Image Tool - BIM 2025 Grade
 * 
 * Places Raster Images (JPG, PNG, BMP) into views.
 * Supports:
 * - Drag & Drop placement
 * - Scaling and Aspect Ratio lock
 * - Brightness/Transparency control
 * - Draw Order (Bring to Front/Send to Back)
 * - Reference for tracing
 */

import { v4 as uuidv4 } from 'uuid';
import { Element } from '../core/types';
import { BaseTool } from './BaseTool';
import { ThreeEvent } from '@react-three/fiber';
import * as THREE from 'three';
import i18n from '../i18n';

export interface ImageGeometryData {
    position: [number, number, number];
    width: number;
    height: number;
    rotation: number;
    sourceUrl: string; // Internal blob URL or path
    opacity: number;
}

export interface ImageElement extends Element {
    category: 'Raster Images';
    geometryData: ImageGeometryData;
}

export class ImageTool extends BaseTool {
    id = 'image_tool';
    name = 'Image';
    cursor = 'default'; // Changes to handling-icon when loading

    private currentImage: { width: number, height: number, url: string, name: string } | null = null;
    private currentPoint: THREE.Vector3 | null = null;

    // Options
    private scale: number = 1.0;
    // private _maintainAspectRatio: boolean = true;
    private opacity: number = 1.0;

    onActivate(): void {
        // BIM: Insert Image opens a file dialog first; the picked raster is
        // read as a data URL, then placed at the next click.
        void this.pickFile('image/png,image/jpeg,image/bmp,image/gif,image/webp').then((file) => {
            if (!file) {
                this.exitToSelect();
                return;
            }
            const reader = new FileReader();
            reader.onload = () => {
                const url = String(reader.result);
                const img = new window.Image();
                img.onload = () => {
                    // Keep the pixel aspect ratio: default physical size is
                    // 1 m on the longer edge, scaled by the aspect.
                    const longest = Math.max(img.width, img.height) || 1000;
                    this.currentImage = {
                        width: (img.width / longest) * 1000,
                        height: (img.height / longest) * 1000,
                        url,
                        name: file.name,
                    };
                    this.notify(i18n.t('tools.notify.imageSelected', { fileName: file.name }));
                };
                img.onerror = () => {
                    // Unknown dimensions: fall back to a 1 m square
                    this.currentImage = { width: 1000, height: 1000, url, name: file.name };
                    this.notify(i18n.t('tools.notify.imageSelected', { fileName: file.name }));
                };
                img.src = url;
            };
            reader.onerror = () => {
                this.notify(i18n.t('tools.notify.imageReadFailed', { fileName: file.name }), 'error');
                this.exitToSelect();
            };
            reader.readAsDataURL(file);
        });
    }

    /**
     * Programmatic load (kept for tests/macros)
     */
    public loadImage(fileMock: { name: string, width: number, height: number }) {
        this.currentImage = {
            width: fileMock.width || 1000, // Def 1m wide if unknown
            height: fileMock.height || 1000,
            url: `assets/mock/${fileMock.name}`,
            name: fileMock.name,
        };
        this.notify(i18n.t('tools.notify.imageLoaded', { fileName: fileMock.name }));
    }

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context || !this.currentImage) return;
        if (event.button !== 0) return;

        const pt = this.getIntersection(event);
        if (pt) {
            this.createImageElement(pt);
            // Single placement flow (BIM): after the raster lands, exit the
            // tool so the next click uses Select again.
            this.currentImage = null;
            this.context.appStore.setToolPreview(null);
            this.exitToSelect();
        }
    }

    onPointerMove(event: ThreeEvent<PointerEvent>): void {
        if (!this.context || !this.currentImage) return;

        const pt = this.getIntersection(event);
        if (pt) {
            this.currentPoint = pt;
            this.updatePreview();
        }
    }

    private updatePreview() {
        if (!this.context || !this.currentImage || !this.currentPoint) return;

        this.context.appStore.setToolPreview({
            type: 'image_rect',
            position: this.currentPoint,
            width: this.currentImage.width * this.scale / 1000, // mm to m
            height: this.currentImage.height * this.scale / 1000,
            rotation: 0 // Default
        });
    }

    private createImageElement(position: THREE.Vector3) {
        if (!this.context || !this.currentImage) return;

        const geometryData: ImageGeometryData = {
            position: position.toArray() as [number, number, number],
            width: this.currentImage.width * this.scale / 1000, // mm to m (scene units)
            height: this.currentImage.height * this.scale / 1000,
            rotation: 0,
            sourceUrl: this.currentImage.url,
            opacity: this.opacity
        };

        const element: ImageElement = {
            id: uuidv4(),
            category: 'Raster Images',
            type: this.currentImage.name,
            parameters: {
                'Width': geometryData.width,
                'Height': geometryData.height,
                'Source': this.currentImage.url,
                'Scale': this.scale
            },
            constraints: [], // View specific typically, but we treat as model element for now
            geometryData
        };

        this.context.bimStore.addElement(element);
        this.notify(i18n.t('tools.notify.imagePlaced', { fileName: this.currentImage.name }), 'success');
    }

    // Helpers
    public setScale(s: number) { this.scale = s; }

    onPointerUp() { }
}
